// Utilities for storing and restoring user state in localStorage.

export interface UserPreferences {
  selectedAccountId: number | string | null;
  selectedAccountName: string | null;
  visibleTableColumns: string[] | null;
  tableMode?: 'models' | 'advertisements';
  isFastMode?: boolean;
  lastViewedDashboardDateRange: number;
  lastViewedTableDateRange: number;
  lastViewedComparisonDateRange: number;
  realtimeThresholds?: {
    balanceWarning: number;
    balanceCritical: number;
    walletWarning: number;
    walletCritical: number;
  };
  accountSettings?: Record<string, any>;
  groupsSettings?: Record<string, any>;
  daysOff?: number;
  realtimeBatchScreenshot?: {
    excludedAccountNames?: string[];
  };
}

export interface RealtimeBatchScreenshotSettings {
  excludedAccountNames: string[];
}

const STORAGE_KEY = 'avito-analytics-user-preferences';

const normalizeAccountNames = (accountNames: string[] | null | undefined): string[] => {
  if (!Array.isArray(accountNames)) return [];

  const seen = new Set<string>();
  const normalized: string[] = [];

  accountNames.forEach((name) => {
    const trimmed = String(name ?? '').trim();
    if (!trimmed) return;

    const key = trimmed.toLowerCase();
    if (seen.has(key)) return;

    seen.add(key);
    normalized.push(trimmed);
  });

  return normalized;
};

export const StorageManager = {
  _cache: null as UserPreferences | null,
  _cacheTimestamp: 0,
  _cacheExpiry: 1000,

  getPreferences(): UserPreferences {
    try {
      const now = Date.now();
      if (this._cache && (now - this._cacheTimestamp) < this._cacheExpiry) {
        return this._cache;
      }

      const stored = localStorage.getItem(STORAGE_KEY);
      let result = stored ? { ...this.getDefaultPreferences(), ...JSON.parse(stored) } : this.getDefaultPreferences();

      result = this._cleanupLegacyData(result);

      this._cache = result;
      this._cacheTimestamp = now;

      return result;
    } catch (error) {
      console.warn('Error reading from localStorage:', error);
      return this.getDefaultPreferences();
    }
  },

  _cleanupLegacyData(preferences: any): UserPreferences {
    if ('comparisonAccounts' in preferences) {
      delete preferences.comparisonAccounts;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
      } catch (error) {
        console.warn('Error saving cleaned preferences:', error);
      }
    }

    try {
      localStorage.removeItem('comparisonAccounts');
    } catch (error) {
      console.warn('Error removing legacy comparison data:', error);
    }

    return preferences;
  },

  savePreferences(preferences: Partial<UserPreferences>): void {
    try {
      const current = this.getPreferences();
      const updated = { ...current, ...preferences };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));

      this._cache = updated;
      this._cacheTimestamp = Date.now();
    } catch (error) {
      console.warn('Error saving to localStorage:', error);
    }
  },

  getDefaultPreferences(): UserPreferences {
    return {
      selectedAccountId: null,
      selectedAccountName: null,
      visibleTableColumns: null,
      lastViewedDashboardDateRange: 30,
      lastViewedTableDateRange: 30,
      lastViewedComparisonDateRange: 30,
      realtimeThresholds: {
        balanceWarning: 15000,
        balanceCritical: 10000,
        walletWarning: 5000,
        walletCritical: 2000,
      },
    };
  },

  clearPreferences(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
      this._cache = null;
      this._cacheTimestamp = 0;
    } catch (error) {
      console.warn('Error clearing localStorage:', error);
    }
  },

  updatePreference<T extends keyof UserPreferences>(
    key: T,
    value: UserPreferences[T],
  ): void {
    if (this._cache) {
      this._cache[key] = value;
      this._cacheTimestamp = Date.now();
    }
    this.savePreferences({ [key]: value });
  },

  getPreference<T extends keyof UserPreferences>(key: T): UserPreferences[T] {
    const now = Date.now();
    if (this._cache && (now - this._cacheTimestamp) < this._cacheExpiry) {
      return this._cache[key];
    }

    const preferences = this.getPreferences();
    return preferences[key];
  },

  async getAccountSettings(accountName: string): Promise<any> {
    try {
      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`/api/v1/settings/${encodeURIComponent(accountName)}`, { headers });
      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch (error) {
      console.warn('Error reading from server:', error);
      return null;
    }
  },

  async saveAccountSettings(accountName: string, settings: any): Promise<void> {
    try {
      const settingsWithMeta = {
        ...settings,
        accountName,
        updatedAt: new Date().toISOString(),
      };

      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`/api/v1/settings/${encodeURIComponent(accountName)}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(settingsWithMeta),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log('Settings saved on server for account:', accountName);
    } catch (error) {
      console.error('Error saving settings on server:', error);
      throw error;
    }
  },

  async getAllAccountSettings(): Promise<Record<string, any>> {
    try {
      const response = await fetch('/api/v1/settings');
      if (response.ok) {
        const data = await response.json();
        console.log('Account settings loaded from server');
        return data;
      }
    } catch (error) {
      console.warn('Error reading settings from server:', error);
    }

    const preferences = this.getPreferences();
    return preferences.accountSettings || {};
  },

  async deleteAccountSettings(accountName: string): Promise<void> {
    try {
      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`/api/v1/settings/${encodeURIComponent(accountName)}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log('Settings deleted on server for account:', accountName);
    } catch (error) {
      console.error('Error deleting settings on server:', error);
      throw error;
    }
  },

  async resetAccountSettings(accountName: string, defaultSettings: any): Promise<void> {
    await this.deleteAccountSettings(accountName);
  },

  getGroupSettings(groupName: string): any {
    const preferences = this.getPreferences();
    return preferences.groupsSettings?.[groupName] || null;
  },

  saveGroupSettings(groupName: string, settings: any): void {
    const preferences = this.getPreferences();
    const groupsSettings = preferences.groupsSettings || {};

    groupsSettings[groupName] = {
      ...settings,
      groupName,
      updatedAt: new Date().toISOString(),
    };

    this.savePreferences({ groupsSettings });
  },

  getAllGroupsSettings(): Record<string, any> {
    const preferences = this.getPreferences();
    return preferences.groupsSettings || {};
  },

  getRealtimeBatchScreenshotSettings(): RealtimeBatchScreenshotSettings {
    const preferences = this.getPreferences();
    return {
      excludedAccountNames: normalizeAccountNames(
        preferences.realtimeBatchScreenshot?.excludedAccountNames,
      ),
    };
  },

  getRealtimeBatchScreenshotExcludedAccountNames(): string[] {
    return this.getRealtimeBatchScreenshotSettings().excludedAccountNames;
  },

  saveRealtimeBatchScreenshotSettings(settings: Partial<RealtimeBatchScreenshotSettings>): void {
    const nextSettings: RealtimeBatchScreenshotSettings = {
      excludedAccountNames: normalizeAccountNames(settings.excludedAccountNames),
    };

    this.savePreferences({
      realtimeBatchScreenshot: nextSettings,
    });
  },

  setRealtimeBatchScreenshotExcludedAccountNames(accountNames: string[]): void {
    this.saveRealtimeBatchScreenshotSettings({ excludedAccountNames: accountNames });
  },

  addRealtimeBatchScreenshotExcludedAccountName(accountName: string): void {
    const current = this.getRealtimeBatchScreenshotExcludedAccountNames();
    this.saveRealtimeBatchScreenshotSettings({
      excludedAccountNames: [...current, accountName],
    });
  },

  removeRealtimeBatchScreenshotExcludedAccountName(accountName: string): void {
    const normalizedTarget = String(accountName ?? '').trim().toLowerCase();
    const current = this.getRealtimeBatchScreenshotExcludedAccountNames();
    this.saveRealtimeBatchScreenshotSettings({
      excludedAccountNames: current.filter((name) => name.toLowerCase() !== normalizedTarget),
    });
  },

  clearRealtimeBatchScreenshotExcludedAccountNames(): void {
    this.saveRealtimeBatchScreenshotSettings({ excludedAccountNames: [] });
  },

  getDaysOff(): number {
    return this.getPreferences().daysOff ?? 0;
  },

  setDaysOff(n: number): void {
    this.updatePreference('daysOff', Math.max(0, Math.min(7, n)));
  },

  deleteGroupSettings(groupName: string): void {
    const preferences = this.getPreferences();
    const groupsSettings = preferences.groupsSettings || {};

    delete groupsSettings[groupName];

    this.savePreferences({ groupsSettings });
  },
};
